import { Card, CardContent } from '@mlm/ui';
import { emptyUnilevelConfig, UnilevelEditor, type UnilevelConfig } from './unilevel-editor';
import { BinaryEditor, type BinaryStoredConfig } from './binary-editor';
import { emptyStairstepConfig, StairstepEditor, type StairstepConfig } from './stairstep-editor';
import {
  emptyAustralianPassUpConfig,
  AustralianPassUpEditor,
  type AustralianPassUpConfig,
} from './australian-pass-up-editor';
import { emptyVortexConfig, VortexEditor, type VortexConfig } from './vortex-editor';
import { emptyGiveMeConfig, GiveMeEditor, type GiveMeConfig } from './give-me-editor';
import {
  emptyRealtimeUnilevelConfig,
  RealtimeUnilevelEditor,
  type RealtimeUnilevelConfig,
} from './realtime-unilevel-editor';

/**
 * Phase 12.5 ComPlan structured editor. Each supported plan has its own
 * form component; unrecognized plan codes fall back to a JSON view so the
 * admin can still see what's there (read-only) until a typed editor is
 * added.
 */
export function CompPlanStructuredEditor({
  planCode,
  value,
  onChange,
  readOnly,
}: {
  planCode: string;
  value: Record<string, unknown>;
  onChange: (next: Record<string, unknown>) => void;
  readOnly?: boolean;
}) {
  switch (planCode) {
    case 'unilevel':
      return (
        <UnilevelEditor
          value={{ ...emptyUnilevelConfig(), ...(value as Partial<UnilevelConfig>) }}
          onChange={(v) => onChange(v as unknown as Record<string, unknown>)}
          readOnly={readOnly}
        />
      );
    case 'binary':
      return (
        <BinaryEditor
          value={value as BinaryStoredConfig}
          onChange={(v) => onChange(v as unknown as Record<string, unknown>)}
          readOnly={readOnly}
        />
      );
    case 'stairstep':
      return (
        <StairstepEditor
          value={{ ...emptyStairstepConfig(), ...(value as Partial<StairstepConfig>) }}
          onChange={(v) => onChange(v as unknown as Record<string, unknown>)}
          readOnly={readOnly}
        />
      );
    case 'australian_pass_up':
      return (
        <AustralianPassUpEditor
          value={{
            ...emptyAustralianPassUpConfig(),
            ...(value as Partial<AustralianPassUpConfig>),
          }}
          onChange={(v) => onChange(v as unknown as Record<string, unknown>)}
          readOnly={readOnly}
        />
      );
    case 'vortex':
      return (
        <VortexEditor
          value={{ ...emptyVortexConfig(), ...(value as Partial<VortexConfig>) }}
          onChange={(v) => onChange(v as unknown as Record<string, unknown>)}
          readOnly={readOnly}
        />
      );
    case 'give_me':
      return (
        <GiveMeEditor
          value={{ ...emptyGiveMeConfig(), ...(value as Partial<GiveMeConfig>) }}
          onChange={(v) => onChange(v as unknown as Record<string, unknown>)}
          readOnly={readOnly}
        />
      );
    case 'realtime_unilevel':
      return (
        <RealtimeUnilevelEditor
          value={{
            ...emptyRealtimeUnilevelConfig(),
            ...(value as Partial<RealtimeUnilevelConfig>),
          }}
          onChange={(v) => onChange(v as unknown as Record<string, unknown>)}
          readOnly={readOnly}
        />
      );
    default:
      return <UnknownPlanFallback config={value} />;
  }
}

function UnknownPlanFallback({ config }: { config: Record<string, unknown> }) {
  return (
    <Card>
      <CardContent className="p-4 space-y-2">
        <p className="text-xs text-muted-foreground">
          No structured editor for this plan yet. Add one in{' '}
          <code className="font-mono">apps/admin/src/components/compplan/</code> to make it
          editable.
        </p>
        <pre className="text-[10px] font-mono bg-muted/40 border rounded p-2 overflow-x-auto">
          {JSON.stringify(config, null, 2)}
        </pre>
      </CardContent>
    </Card>
  );
}

/** Pretty display label for the plan code in the sidebar selector. */
export function planDisplayName(code: string): string {
  switch (code) {
    case 'unilevel':
      return 'Unilevel';
    case 'realtime_unilevel':
      return 'Realtime Unilevel';
    case 'binary':
      return 'Binary';
    case 'binary_mentor':
      return "Binary Mentor's Bonus";
    case 'stairstep':
      return 'Stairstep';
    case 'australian_pass_up':
      return 'Australian pass-up';
    case 'sign_up_bonus':
      return 'Sign-up bonus';
    case 'sign_up_vortex':
      return 'Sign-up vortex';
    case 'direct':
      return 'Direct (per-tier)';
    case 'direct_kit_price':
      return 'Direct (kit price %)';
    case 'indirect':
      return 'Indirect (per-tier)';
    case 'mentor_bonus':
      return 'Legacy Mentor bonus';
    case 'monthly_direct_bonus':
      return 'Monthly direct bonus';
    case 'unilevel_consecutive_bonus':
      return 'Unilevel consecutive bonus';
    case 'infinity_bonus':
      return 'Infinity bonus on activation';
    case 'vortex':
      return 'Vortex';
    case 'give_me':
      return 'Give Me';
    case 'maintenance_cashback':
      return 'Cashback Bonus';
    default:
      return code.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
  }
}

/** Plain-English summary of what each plan pays + when it fires. */
export function planDescription(code: string): string {
  switch (code) {
    case 'unilevel':
      return 'Pays a percentage of every repurchase up multiple sponsor levels. Optional personal-PV qualification; compression rolls a skipped level’s share up to the next qualified sponsor.';
    case 'realtime_unilevel':
      return 'Pays a FIXED cash amount per level the moment a member buys a configured trigger product — credited straight to the wallet. Level 0 is the buyer’s own cashback; Levels 1+ pay the sponsor uplines. No maintenance or qualification; amounts scale by quantity.';
    case 'binary':
      return 'Builds left/right leg points and pays a fixed amount each time the legs pair. The active variant sets how points are earned, qualified, and capped.';
    case 'stairstep':
      return 'At period close, ranks each slot from its PPV / GPV / legs / directs and pays the difference between the earner’s rank rate and each downline’s rate on their volume (a differential override).';
    case 'australian_pass_up':
      return 'A new member’s first sale passes up to their sponsor; later sales stay with the seller. A per-tier pass-up sponsoring bonus.';
    case 'vortex':
      return 'A shared cycling queue. Tokens (from products or activation) buy queue entries; every Nth entry graduates the oldest one, paying a cash + Gift-Cert reward and re-entering it.';
    case 'sign_up_bonus':
      return 'A one-time cash bonus paid to a new member’s slot when it activates, set by that new slot’s own tier.';
    case 'sign_up_vortex':
      return 'A second one-time activation bonus paid in parallel to the new slot. Use it alongside or instead of the sign-up bonus.';
    case 'direct':
      return 'Pays the direct sponsor a flat amount when someone they sponsored activates, looked up by the sponsor’s tier × the new member’s tier.';
    case 'direct_kit_price':
      return 'Pays the direct sponsor a PERCENTAGE of the discounted kit price the new member actually paid, when someone they sponsored activates. Percent is set per sponsor tier × new-member tier. Independent of the flat Direct bonus.';
    case 'indirect':
      return 'Like direct, but pays sponsors further up the line (level 2 and beyond) a flat amount per level when a downline activates.';
    case 'mentor_bonus':
      return 'Pays uplines a percentage of a member’s repurchase across several levels, with a minimum-directs requirement per level and optional per-period caps.';
    case 'monthly_direct_bonus':
      return 'At period close, awards points for each qualifying direct who purchased that period, gated by minimum directs and personal PV.';
    case 'unilevel_consecutive_bonus':
      return 'Rewards a member who stays qualified for several consecutive periods; the longer the streak, the larger the bonus.';
    case 'infinity_bonus':
      return 'On activation, a fixed bonus base (set per tier) cascades up the sponsor line; each higher-ranked upline earns the extra percentage its rank adds (an infinity-style override).';
    case 'give_me':
      return 'Pays the direct sponsor a reward each time their lifetime count of PAID directs of a given tier reaches a multiple of N. Each direct tier is counted separately; the reward and N are set per sponsor-tier × direct-tier cell, across one or more GM Settings.';
    case 'maintenance_cashback':
      return 'Pays a member back a percentage of their OWN personal PV (PPV) as cash, provided they hit their tier’s minimum PPV (maintenance) for the window. Both the percentage and the minimum are set per tier. Run as an admin date-range distribution.';
    default:
      return '';
  }
}

/**
 * Plans whose config lives in `comp_plan_configs` (one row per plan,
 * versioned, edited by /compplan via `publishCompPlanConfig`).
 */
export const FLAT_PLAN_CODES = [
  'unilevel',
  'realtime_unilevel',
  'binary',
  'stairstep',
  'australian_pass_up',
  'vortex',
  'give_me',
] as const;

/**
 * Plans whose config lives PER-TIER inside `memberships.config`. The
 * /compplan editor loads every membership, presents the values across all
 * tiers, and writes back per-tier.
 */
export const PER_TIER_PLAN_CODES = [
  'sign_up_bonus',
  'sign_up_vortex',
  'direct',
  'direct_kit_price',
  'indirect',
  'mentor_bonus',
  'monthly_direct_bonus',
  'unilevel_consecutive_bonus',
  'infinity_bonus',
  'maintenance_cashback',
] as const;

export const SUPPORTED_PLAN_CODES = [...FLAT_PLAN_CODES, ...PER_TIER_PLAN_CODES] as const;

/**
 * Keys accepted by the Memberships → Lifetime hard caps editor. Superset of
 * the plan codes: `binary_mentor` is not a plan — it's the Binary Mentor's
 * Bonus rider's DEDICATED lifetime pool (mentor cash also counts inside the
 * shared `binary` total; the tighter cap wins). Kept out of
 * `SUPPORTED_PLAN_CODES` so it never shows in the ComPlan editor or the
 * daily-cap-group checkboxes.
 */
export const HARD_CAP_PLAN_CODES = [
  ...FLAT_PLAN_CODES.flatMap((c) => (c === 'binary' ? ([c, 'binary_mentor'] as const) : [c])),
  ...PER_TIER_PLAN_CODES,
] as const;

export function isPerTierPlan(code: string): boolean {
  return (PER_TIER_PLAN_CODES as readonly string[]).includes(code);
}

/**
 * Which trigger fires each plan. Used to group plans in both the ComPlan
 * editor sidebar and the Settings → System "Comp plans" toggle card so the
 * operator can see WHEN each one runs at a glance.
 *
 * `binary` lives under `placement` (its headline trigger) even though it
 * still also handles `slot_activated` and `order_completed`; the full
 * `slot_activated` ↔ `slot_placed` event split is tracked separately under
 * memory `mlm-slot-placement-separation`.
 */
export type PlanTrigger = 'activation' | 'placement' | 'purchase' | 'period_close';

export const PLAN_TRIGGER: Record<string, PlanTrigger> = {
  // Slot Activation — fires when a member's slot is created/activated.
  sign_up_bonus: 'activation',
  sign_up_vortex: 'activation',
  direct: 'activation',
  direct_kit_price: 'activation',
  indirect: 'activation',
  australian_pass_up: 'activation',
  infinity_bonus: 'activation',
  vortex: 'activation',
  give_me: 'activation',
  // Slot Placement — fires when a slot is placed on the binary tree.
  binary: 'placement',
  // Product Repurchase — fires on non-kit orders.
  unilevel: 'purchase',
  realtime_unilevel: 'purchase',
  mentor_bonus: 'purchase',
  // Period Close — admin-batched at period end.
  stairstep: 'period_close',
  monthly_direct_bonus: 'period_close',
  unilevel_consecutive_bonus: 'period_close',
  maintenance_cashback: 'period_close',
};

export const TRIGGER_LABEL: Record<PlanTrigger, string> = {
  activation: 'Slot Activation',
  placement: 'Slot Placement',
  purchase: 'Product Repurchase',
  period_close: 'Period Close',
};

/** Rendering order for trigger groups in both editor sidebar and settings card. */
export const TRIGGER_ORDER: readonly PlanTrigger[] = [
  'activation',
  'placement',
  'purchase',
  'period_close',
] as const;

export function planTrigger(code: string): PlanTrigger {
  return PLAN_TRIGGER[code] ?? 'activation';
}
